import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  FileSearch,
  Ruler,
  Palette,
  Hammer,
  Factory,
  CheckCircle,
  ArrowRight,
} from "lucide-react";
import SectionHeading from "../components/common/SectionHeading";
import AnimatedSection from "../components/common/AnimatedSection";
import Button from "../components/common/Button";
import { useBookingModal } from "../contexts/BookingModalContext";
import drillImage from "../assets/images/DRILL.webp";

gsap.registerPlugin(ScrollTrigger);

const processSteps = [
  {
    number: "01",
    title: "Discovery & Brief",
    description:
      "Every great interior begins with understanding. We sit with you to learn about your lifestyle, preferences, family needs, and budget. For B2B partners, we dive deep into project specifications, volumes, and technical requirements.",
    details: [
      "Understand your vision and requirements",
      "Discuss budget expectations",
      "Share initial design possibilities",
      "Define project timeline",
    ],
    duration: "Day 1-2",
    icon: FileSearch,
    image:
      "https://images.unsplash.com/photo-1600494603989-9650cf6ddd3d?w=800&q=80",
  },
  {
    number: "02",
    title: "Site Visit & Measure",
    description:
      "Our technical team visits your site with precision instruments. Every wall, every corner, every electrical point is measured and documented. This data becomes the foundation for perfect-fit modular solutions.",
    details: [
      "Comprehensive site survey",
      "Precise digital measurements",
      "Electrical and plumbing mapping",
      "Photo documentation",
    ],
    duration: "Day 2-3",
    icon: Ruler,
    image:
      "https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?w=800&q=80",
  },
  {
    number: "03",
    title: "Design & Alignment",
    description:
      "This is where your space comes to life – first on screen, then in reality. Our designers create detailed layouts, 3D visualizations, and material boards. We iterate until every element aligns with your vision.",
    details: [
      "2D layouts and floor plans",
      "Photorealistic 3D renders",
      "Material and color selection",
      "Multiple revision rounds",
    ],
    duration: "Day 3-6",
    icon: Palette,
    image:
      "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=800&q=80",
  },
  {
    number: "04",
    title: "Material & Hardware Sign-off",
    description:
      "Quality begins with the right materials. We help you choose between MR Ply, BWR Ply, BWP Ply, and HDHMR based on room usage and moisture exposure. Hardware selection includes premium soft-close mechanisms and durable fittings.",
    details: [
      "MR/BWR/BWP Ply selection by room",
      "HDHMR for specific applications",
      "Premium hardware options",
      "Finish and laminate samples",
    ],
    duration: "Day 6-7",
    icon: Hammer,
    image:
      "https://images.unsplash.com/photo-1622372738946-62e02505feb3?w=800&q=80",
  },
  {
    number: "05",
    title: "Factory Production",
    description:
      "Your approved designs enter our 10,000+ sq. ft factory. Biesse machines precision-cut every panel to sub-millimeter accuracy. MR/BWR/BWP Ply and HDHMR boards are edge-banded, drilled, and quality-checked before packaging.",
    details: [
      "CNC cutting on Biesse machines",
      "Automated edge-banding",
      "Precision drilling and hardware prep",
      "Multi-point quality inspection",
    ],
    duration: "Day 8-15",
    icon: Factory,
    image:
      drillImage,
  },
  {
    number: "06",
    title: "Installation & Handover",
    description:
      "Our trained installation team brings your modules to life. Clean, efficient assembly with minimal disruption. After installation, a detailed walkthrough ensures every door, drawer, and fitting works perfectly.",
    details: [
      "Professional assembly team",
      "Clean installation process",
      "Hardware adjustments",
      "Handover walkthrough and tips",
    ],
    duration: "Day 16-20",
    icon: CheckCircle,
    image:
      "https://images.unsplash.com/photo-1600210491892-03d54c0aaf87?w=800&q=80",
  },
];

export default function ProcessPage() {
  const sectionRef = useRef(null);
  const { openModal } = useBookingModal();

  useEffect(() => {
    if (!sectionRef.current) return;

    const element = sectionRef.current;
    let ctx = null;

    try {
      ctx = gsap.context(() => {
        gsap.utils.toArray(".process-step").forEach((step, i) => {
          gsap.from(step, {
            opacity: 0,
            y: 50,
            duration: 0.8,
            scrollTrigger: {
              trigger: step,
              start: "top 80%",
              toggleActions: "play none none reverse",
            },
          });
        });
      }, element);

      return () => {
        if (ctx) {
          try {
            // Kill ALL ScrollTriggers immediately to unpin any pinned elements
            // This must be synchronous to prevent React from trying to remove moved nodes
            const allTriggers = ScrollTrigger.getAll();
            allTriggers.forEach((trigger) => {
              try {
                trigger.kill();
              } catch (e) {
                // Ignore individual trigger kill errors
              }
            });
            ctx.revert();
          } catch (error) {
            // Silently ignore cleanup errors - React may have already unmounted
            console.warn("GSAP cleanup error (safe to ignore):", error);
          }
        }
      };
    } catch (error) {
      console.warn("GSAP animation setup error:", error);
    }
  }, []);

  return (
    <div ref={sectionRef}>
      {/* Hero */}
      <section className="relative py-32 lg:py-40 bg-gradient-brand overflow-hidden">
        <div className="container-custom section-padding relative">
          <AnimatedSection className="max-w-3xl">
            <span className="text-brand-accent text-sm font-medium uppercase tracking-wider">
              The 20-Day Promise
            </span>
            <h1 className="font-heading text-display-1 text-white mt-4 mb-6">
              Your modular journey, mapped in 6 clear steps.
            </h1>
            <p className="text-xl text-white/80 leading-relaxed">
              From first conversation to final handover, every step is designed
              for transparency, precision, and your peace of mind.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Timeline */}
      <section className="section-spacing bg-background-main">
        <div className="container-custom section-padding">
          <div className="space-y-24">
            {processSteps.map((step, index) => (
              <div
                key={step.number}
                className={`process-step grid lg:grid-cols-2 gap-12 lg:gap-16 items-center ${
                  index % 2 === 1 ? "lg:flex-row-reverse" : ""
                }`}
              >
                {/* Content */}
                <div className={index % 2 === 1 ? "lg:order-2" : ""}>
                  <div className="inline-flex items-center px-4 py-1.5 bg-brand-accent/10 rounded-full text-brand-accent text-sm font-medium mb-4">
                    {step.duration}
                  </div>

                  <h2 className="font-heading text-h2 text-brand-primary mb-4">
                    {step.title}
                  </h2>
                  <p className="text-text-secondary text-lg leading-relaxed mb-6">
                    {step.description}
                  </p>

                  <ul className="space-y-3">
                    {step.details.map((detail, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div className="w-5 h-5 bg-brand-accent/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <div className="w-2 h-2 bg-brand-accent rounded-full" />
                        </div>
                        <span className="text-text-primary">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Image */}
                <div className={index % 2 === 1 ? "lg:order-1" : ""}>
                  <div className="relative">
                    <div className="aspect-[4/3] rounded-3xl overflow-hidden">
                      <img
                        src={step.image}
                        alt={step.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {/* Step number badge */}
                    <div className="absolute -bottom-4 -right-4 w-20 h-20 bg-brand-accent rounded-2xl flex items-center justify-center shadow-large">
                      <span className="font-heading text-3xl text-white">
                        {step.number}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-brand">
        <div className="container-custom section-padding text-center">
          <AnimatedSection>
            <h2 className="font-heading text-h2 text-white mb-6">
              Ready to start your 20-day journey?
            </h2>
            <p className="text-xl text-white/80 max-w-2xl mx-auto mb-10">
              Book a consultation with our design experts. We'll understand your
              needs and create a personalized plan for your space.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={openModal} variant="white" size="large">
                Book Consultation
              </Button>
              <Button
                href={`https://wa.me/${
                  process.env.REACT_APP_WHATSAPP_NUMBER || "917995659797"
                }`}
                variant="outline"
                size="large"
              >
                WhatsApp Us
              </Button>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
